import {makeAutoObservable, observable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import type {IRanking} from "data/providers/api/ranking.api.provider";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {ISquad, ISquadsStore} from "data/stores/squads/squads.store";
import type {IPlayer, IPlayersStore} from "data/stores/players/players.store";
import {forEach} from "lodash";
import {SPECIAL_SYMBOLS} from "data/constants";
import {LeaderboardType} from "data/enums";
import type {IRankingStore} from "data/stores/ranking/ranking.store";

interface IProps {
	ranking: IRanking;
}

export interface ILeaderboardRowController extends ViewController<IProps> {
	readonly i18n: ILocalizationStore;

	get currentSquad(): ISquad | undefined;
	get currentPlayer(): IPlayer | undefined;
	get playerExternalLink(): string;
	get selectedLeaderboard(): LeaderboardType;
	get isPersonalLeaderboard(): boolean;
}

@injectable()
export class LeaderboardRowController implements ILeaderboardRowController {
	@observable _ranking: IRanking | null = null;

	get currentSquad() {
		if (!this._ranking?.squadId) return undefined;

		return this._squadsStore.getSquadById(this._ranking.squadId);
	}

	get currentPlayer() {
		if (!this._ranking?.playerId) return undefined;

		return this._playersStore.getPlayerById(this._ranking.playerId);
	}

	get playerExternalLink() {
		if (!this.currentPlayer) return "";

		let firstName = this.currentPlayer.firstName.toLowerCase();
		let lastName = this.currentPlayer.lastName.toLowerCase();

		forEach(SPECIAL_SYMBOLS, (item, key) => {
			firstName = firstName.replace(key, item);
			lastName = lastName.replace(key, item);
		});

		const fullName = `${firstName}-${lastName}`;
		const playerName = fullName
			.trim()
			.replaceAll(".", "")
			.replaceAll(" ", "-")
			.replaceAll("'", "-");

		return `https://www.mlb.com/player/${playerName}-${this.currentPlayer.feedId}`;
	}

	get selectedLeaderboard() {
		return this._rankingStore.selectedLeaderboard;
	}

	get isPersonalLeaderboard() {
		return this.selectedLeaderboard === LeaderboardType.PERSONAL;
	}

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.SquadsStore) public _squadsStore: ISquadsStore,
		@inject(Bindings.PlayersStore) public _playersStore: IPlayersStore,
		@inject(Bindings.RankingStore) public _rankingStore: IRankingStore
	) {
		makeAutoObservable(this);
	}

	init({ranking}: IProps) {
		this._ranking = ranking;
	}
}
